import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  Drawer, Box, Typography, IconButton, Divider, Button, TextField,
  Paper, Grid, Select, MenuItem, FormControl, InputLabel, Checkbox,
  FormControlLabel, Stack, CircularProgress, Alert, Popover,
} from '@mui/material';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import dayjs from 'dayjs';
import { configApi } from '../../../services/configApi';
import { formatMoney, formatDate, toPercent, toSmallestUnit, fromPercent } from '../../../utils/configUtils';

const brandTheme = createTheme({
  palette: { primary: { main: '#065F46' } },
});

const TAX_TYPES = ['SalesTax', 'GST', 'PST', 'HST', 'VAT'];
const SHIP_METHODS = ['standard', 'express', 'two_day', 'next_day', 'pickup'];
const formatMethod = (v) => v.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
const APPLY_TO_OPTS = ['subtotal', 'order_total'];

const CURRENCIES = [
  { code: 'USD', name: 'US Dollar' },
  { code: 'EUR', name: 'Euro' },
  { code: 'GBP', name: 'British Pound' },
  { code: 'INR', name: 'Indian Rupee' }, // kept for legacy data
  { code: 'AUD', name: 'Australian Dollar' },
  { code: 'CAD', name: 'Canadian Dollar' },
  { code: 'SGD', name: 'Singapore Dollar' },
  { code: 'AED', name: 'UAE Dirham' },
  { code: 'JPY', name: 'Japanese Yen' },
  { code: 'CNY', name: 'Chinese Yuan' },
  { code: 'CHF', name: 'Swiss Franc' },
  { code: 'HKD', name: 'Hong Kong Dollar' },
  { code: 'NZD', name: 'New Zealand Dollar' },
  { code: 'SEK', name: 'Swedish Krona' },
  { code: 'NOK', name: 'Norwegian Krone' },
  { code: 'MXN', name: 'Mexican Peso' },
  { code: 'BRL', name: 'Brazilian Real' },
  { code: 'ZAR', name: 'South African Rand' },
  { code: 'MYR', name: 'Malaysian Ringgit' },
  { code: 'PHP', name: 'Philippine Peso' },
  { code: 'THB', name: 'Thai Baht' },
  { code: 'IDR', name: 'Indonesian Rupiah' },
  { code: 'SAR', name: 'Saudi Riyal' },
  { code: 'QAR', name: 'Qatari Riyal' },
  { code: 'KWD', name: 'Kuwaiti Dinar' },
  { code: 'BHD', name: 'Bahraini Dinar' },
  { code: 'OMR', name: 'Omani Rial' },
  { code: 'EGP', name: 'Egyptian Pound' },
  { code: 'PKR', name: 'Pakistani Rupee' },
  { code: 'BDT', name: 'Bangladeshi Taka' },
  { code: 'LKR', name: 'Sri Lankan Rupee' },
  { code: 'NPR', name: 'Nepalese Rupee' },
  { code: 'NGN', name: 'Nigerian Naira' },
  { code: 'KES', name: 'Kenyan Shilling' },
  { code: 'GHS', name: 'Ghanaian Cedi' },
  { code: 'TZS', name: 'Tanzanian Shilling' },
  { code: 'UGX', name: 'Ugandan Shilling' },
  { code: 'RUB', name: 'Russian Ruble' },
  { code: 'TRY', name: 'Turkish Lira' },
  { code: 'PLN', name: 'Polish Zloty' },
  { code: 'CZK', name: 'Czech Koruna' },
  { code: 'HUF', name: 'Hungarian Forint' },
  { code: 'RON', name: 'Romanian Leu' },
  { code: 'DKK', name: 'Danish Krone' },
  { code: 'ILS', name: 'Israeli Shekel' },
  { code: 'CLP', name: 'Chilean Peso' },
  { code: 'COP', name: 'Colombian Peso' },
  { code: 'PEN', name: 'Peruvian Sol' },
  { code: 'ARS', name: 'Argentine Peso' },
  { code: 'VND', name: 'Vietnamese Dong' },
  { code: 'KRW', name: 'South Korean Won' },
  { code: 'TWD', name: 'Taiwan Dollar' },
];

const inputSx = {
  '& .MuiOutlinedInput-root': { borderRadius: '10px', fontFamily: 'Heebo' },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#065F46' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#065F46' },
};

const SECTION_INFO = {
  tax: {
    intro: 'Stack multiple rules to cover different regions. Each rule is evaluated independently.',
    fields: [
      { name: 'Type', desc: 'GST for Canada, VAT for Europe, SalesTax for US states' },
      { name: 'Rate', desc: '0.08 for 8%, 0.1 for 10%, 0.18 for 18%' },
      { name: 'Region', desc: 'US-CA for California only, IN for all of India, blank for every order' },
      { name: 'Zip Code', desc: '10001 to target Manhattan only, blank for the whole region' },
      { name: 'Inclusive', desc: 'Yes: item listed at $108, customer pays $108. No: item at $100, customer pays $108 at checkout' },
      { name: 'Priority', desc: '0 runs before 1. Put federal tax at 0, state tax at 1' },
      { name: 'Active', desc: 'No to pause a seasonal rule without losing the config' },
    ],
  },
  shipping: {
    intro: 'Each active method shows up as a checkout option for the customer.',
    fields: [
      { name: 'Method', desc: 'Standard for 5-7 day delivery, Express for 2-3 days, Next Day for overnight' },
      { name: 'Base Cost', desc: '$4.99 for standard, $12.99 for express, $0 for free pickup' },
      { name: 'Free Above', desc: '$50 means orders over $50 ship free. 0 means always charged' },
      { name: 'Active', desc: 'No to hide Next Day on weekends without removing it' },
    ],
  },
  appfee: {
    intro: 'Applied per order on the seller side. Min and max let you set a floor and ceiling.',
    fields: [
      { name: 'Commission %', desc: '5 means you collect $5 from a $100 order' },
      { name: 'Flat Fee', desc: '$0.30 added on top of the percentage on every order' },
      { name: 'Apply To', desc: 'Subtotal: fee on $100 ignores $10 shipping. Order Total: fee on $110' },
      { name: 'Min Fee', desc: '$0.50 floor means a $3 order still yields $0.50, not $0.15' },
      { name: 'Max Fee', desc: '$25 cap means a $1000 order at 5% yields $25, not $50' },
      { name: 'Active', desc: 'No to waive fees platform-wide during a promo' },
    ],
  },
};

const SectionLabel = ({ children }) => (
  <Typography sx={{
    fontFamily: 'Heebo', fontWeight: 600, color: '#362B24', opacity: 0.5,
    fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.8, mb: 1.5,
  }}>
    {children}
  </Typography>
);

function SectionHeader({ children, onInfo }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
      <Typography sx={{
        fontFamily: 'Heebo', fontWeight: 600, color: '#362B24', opacity: 0.5,
        fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.8,
      }}>
        {children}
      </Typography>
      <IconButton size="small" onClick={onInfo} sx={{ p: 0.25, color: 'rgba(0,0,0,0.25)', '&:hover': { color: '#065F46', bgcolor: 'transparent' } }}>
        <InfoOutlinedIcon sx={{ fontSize: 13 }} />
      </IconButton>
    </Box>
  );
}

const defaultTax = () => ({
  tax_type: 'GST', rate: '0.18', region: '', zip_code: '', is_inclusive: false, priority: '0', is_active: true,
});
const defaultShipping = () => ({
  method: 'standard', base_cost: '0', free_above: '0', is_active: true,
});
const defaultAppFee = () => ({
  percentage: '5', flat: '0', apply_to: 'subtotal', min_fee: '0', max_fee: '0', is_active: true,
});

function initForm() {
  return { name: '', currency: 'USD', effective_from: null, effective_to: null };
}

function validateForm(form, taxes, appFee) {
  const errs = {};
  if (!form.name.trim()) errs.name = 'Required';
  if (!form.currency) errs.currency = 'Required';
  if (!form.effective_from) errs.effective_from = 'Required';
  if (form.effective_to && form.effective_from && !dayjs(form.effective_to).isAfter(dayjs(form.effective_from))) {
    errs.effective_to = 'Must be after Effective From';
  }
  const pct = parseFloat(appFee.percentage);
  if (isNaN(pct) || pct < 0 || pct > 100) errs.app_fee_pct = 'Must be 0–100';
  const minFee = toSmallestUnit(appFee.min_fee);
  const maxFee = toSmallestUnit(appFee.max_fee);
  if (maxFee > 0 && minFee > maxFee) errs.app_fee_min = 'Min fee exceeds max fee';
  taxes.forEach((t, i) => {
    const r = parseFloat(t.rate);
    if (isNaN(r) || r < 0 || r > 1) errs[`tax_rate_${i}`] = 'Must be 0–1';
  });
  return errs;
}

function buildPayload(form, taxes, shippings, appFee) {
  const payload = {
    name: form.name.trim(),
    currency: form.currency.trim().toUpperCase(),
    effective_from: dayjs(form.effective_from).toISOString(),
    tax: taxes.map((t) => ({
      tax_type: t.tax_type,
      rate: parseFloat(t.rate),
      ...(t.region && { region: t.region }),
      ...(t.zip_code && { zip_code: t.zip_code }),
      is_inclusive: t.is_inclusive,
      priority: parseInt(t.priority) || 0,
      is_active: t.is_active,
    })),
    shipping: shippings.map((s) => ({
      method: s.method,
      base_cost: toSmallestUnit(s.base_cost),
      free_above: toSmallestUnit(s.free_above),
      is_active: s.is_active,
    })),
    app_fee: {
      percentage: fromPercent(appFee.percentage),
      flat: toSmallestUnit(appFee.flat),
      apply_to: appFee.apply_to,
      min_fee: toSmallestUnit(appFee.min_fee),
      max_fee: toSmallestUnit(appFee.max_fee),
      is_active: appFee.is_active,
    },
  };
  if (form.effective_to) payload.effective_to = dayjs(form.effective_to).toISOString();
  return payload;
}

function ViewSection({ label, value }) {
  return (
    <Box>
      <Typography sx={{ fontSize: '11px', opacity: 0.55, fontFamily: 'Heebo', mb: 0.3 }}>{label}</Typography>
      <Typography sx={{ fontFamily: 'Heebo', fontWeight: 500, fontSize: '14px', color: '#362B24' }}>{value}</Typography>
    </Box>
  );
}

function ViewMode({ config, onClose, onActivate, activating }) {
  const c = config;
  const fee = c?.app_fee ?? {};
  const taxes = c?.tax ?? [];
  const shippings = c?.shipping ?? [];
  const cur = c?.currency || 'USD';

  return (
    <>
      <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box>
          <SectionLabel>Basic</SectionLabel>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <ViewSection label="Version Code" value={c.version_code} />
            <ViewSection label="Name" value={c.name} />
            <ViewSection label="Currency" value={c.currency} />
            <ViewSection label="Effective From" value={formatDate(c.effective_from)} />
            <ViewSection label="Effective To" value={formatDate(c.effective_to)} />
            <ViewSection label="Updated By" value={c.updated_by?.name || '—'} />
          </Box>
        </Box>

        {taxes.length > 0 && (
          <Box>
            <SectionLabel>Tax Entries ({taxes.length})</SectionLabel>
            <Stack spacing={1.5}>
              {taxes.map((t, i) => (
                <Paper key={i} variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
                    <ViewSection label="Type" value={t.tax_type} />
                    <ViewSection label="Rate" value={toPercent(t.rate)} />
                    <ViewSection label="Region" value={t.region || '—'} />
                    <ViewSection label="Zip Code" value={t.zip_code || '—'} />
                    <ViewSection label="Inclusive" value={t.is_inclusive ? 'Yes' : 'No'} />
                    <ViewSection label="Priority" value={String(t.priority)} />
                  </Box>
                </Paper>
              ))}
            </Stack>
          </Box>
        )}

        {shippings.length > 0 && (
          <Box>
            <SectionLabel>Shipping Methods ({shippings.length})</SectionLabel>
            <Stack spacing={1.5}>
              {shippings.map((s, i) => (
                <Paper key={i} variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
                    <ViewSection label="Method" value={formatMethod(s.method)} />
                    <ViewSection label="Base Cost" value={formatMoney(s.base_cost, cur)} />
                    <ViewSection label="Free Above" value={s.free_above ? formatMoney(s.free_above, cur) : 'Never free'} />
                    <ViewSection label="Active" value={s.is_active ? 'Yes' : 'No'} />
                  </Box>
                </Paper>
              ))}
            </Stack>
          </Box>
        )}

        {fee && (
          <Box>
            <SectionLabel>App Fee</SectionLabel>
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 1.5 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
                <ViewSection label="Commission" value={toPercent(fee.percentage)} />
                <ViewSection label="Flat Fee" value={formatMoney(fee.flat, cur)} />
                <ViewSection label="Apply To" value={formatMethod(fee.apply_to)} />
                <ViewSection label="Min Fee" value={formatMoney(fee.min_fee, cur)} />
                <ViewSection label="Max Fee" value={fee.max_fee ? formatMoney(fee.max_fee, cur) : 'No cap'} />
                <ViewSection label="Active" value={fee.is_active ? 'Yes' : 'No'} />
              </Box>
            </Paper>
          </Box>
        )}
      </Box>

      <Box sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
        <Button onClick={onClose} sx={{ fontFamily: 'Heebo', textTransform: 'none', color: '#362B24' }}>
          Close
        </Button>
        {!config.is_active && (
          <Button
            variant="contained"
            color="success"
            disabled={activating}
            startIcon={activating ? <CircularProgress size={14} color="inherit" /> : null}
            onClick={() => onActivate(config._id)}
            sx={{ fontFamily: 'Heebo', textTransform: 'none', borderRadius: '10px' }}
          >
            {activating ? 'Activating…' : 'Activate'}
          </Button>
        )}
      </Box>
    </>
  );
}

export default function ConfigDrawer({ open, mode, config, onClose, onSuccess, onActivate, activating }) {
  const token = useSelector((state) => state?.user?.user?.token);
  const [form, setForm] = useState(initForm());
  const [taxes, setTaxes] = useState([]);
  const [shippings, setShippings] = useState([]);
  const [appFee, setAppFee] = useState(defaultAppFee());
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [infoPopover, setInfoPopover] = useState({ anchor: null, section: null });

  useEffect(() => {
    if (!open) {
      setForm(initForm());
      setTaxes([]);
      setShippings([]);
      setAppFee(defaultAppFee());
      setErrors({});
    }
  }, [open]);

  const updateTax = (i, key, val) => setTaxes((prev) => prev.map((t, idx) => idx === i ? { ...t, [key]: val } : t));
  const updateShipping = (i, key, val) => setShippings((prev) => prev.map((s, idx) => idx === i ? { ...s, [key]: val } : s));

  const handleSubmit = async (andActivate = false) => {
    const errs = validateForm(form, taxes, appFee);
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setErrors({});
    setSubmitting(true);
    try {
      const res = await configApi.createConfig(token, buildPayload(form, taxes, shippings, appFee));
      if (andActivate) {
        const newId = res?.data?._id || res?._id;
        if (newId) {
          await configApi.activateConfig(token, newId);
          toast.success('Configuration created and activated.');
        } else {
          toast.success('Configuration created.');
          toast.warning('Could not auto-activate. Activate it manually from the table.');
        }
      } else {
        toast.success('Configuration created.');
      }
      onSuccess();
    } catch (e) {
      toast.error(e?.error || 'Failed to create configuration.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ThemeProvider theme={brandTheme}>
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{ sx: { width: 520, maxWidth: '100vw' } }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Header */}
          <Box sx={{
            px: 3, py: 2.5, borderBottom: '1px solid', borderColor: 'divider',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <Typography sx={{ fontFamily: 'Heebo', fontWeight: 700, fontSize: '16px', color: '#362B24' }}>
              {mode === 'create' ? 'New Platform Configuration' : `${config?.version_code}: ${config?.name}`}
            </Typography>
            <IconButton onClick={onClose} size="small"><CloseOutlinedIcon fontSize="small" /></IconButton>
          </Box>

          {mode === 'view' && config ? (
            <ViewMode config={config} onClose={onClose} onActivate={onActivate} activating={activating} />
          ) : (
            <>
              {/* Create form body */}
              <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>

                {/* Basic */}
                <Box>
                  <SectionLabel>Basic</SectionLabel>
                  <Stack spacing={2}>
                    <TextField
                      label="Config Name"
                      placeholder="e.g. Holiday Season 2025"
                      size="small"
                      fullWidth
                      value={form.name}
                      onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                      required
                      error={!!errors.name}
                      helperText={errors.name || 'A label to identify this configuration'}
                      sx={inputSx}
                    />
                    <FormControl size="small" fullWidth error={!!errors.currency} sx={inputSx}>
                      <InputLabel>Currency *</InputLabel>
                      <Select
                        value={form.currency}
                        label="Currency *"
                        onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))}
                      >
                        {CURRENCIES.map(({ code, name }) => (
                          <MenuItem key={code} value={code} sx={{ fontFamily: 'Heebo' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 2 }}>
                              <Typography sx={{ fontFamily: 'Heebo', fontWeight: 600, fontSize: '13px' }}>{code}</Typography>
                              <Typography sx={{ fontFamily: 'Heebo', fontSize: '13px', opacity: 0.6 }}>{name}</Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.currency && (
                        <Typography sx={{ fontSize: '11px', color: 'error.main', mt: 0.5, ml: 1.5 }}>{errors.currency}</Typography>
                      )}
                    </FormControl>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <DatePicker
                        label="Effective From *"
                        value={form.effective_from}
                        onChange={(v) => setForm((f) => ({ ...f, effective_from: v }))}
                        slotProps={{
                          textField: {
                            size: 'small', sx: inputSx, fullWidth: true,
                            error: !!errors.effective_from, helperText: errors.effective_from,
                          },
                        }}
                      />
                      <DatePicker
                        label="Effective To"
                        value={form.effective_to}
                        onChange={(v) => setForm((f) => ({ ...f, effective_to: v }))}
                        slotProps={{
                          textField: {
                            size: 'small', sx: inputSx, fullWidth: true,
                            error: !!errors.effective_to, helperText: errors.effective_to,
                          },
                        }}
                      />
                    </Box>
                  </Stack>
                </Box>

                {/* Tax Entries */}
                <Box>
                  <SectionHeader onInfo={(e) => setInfoPopover({ anchor: e.currentTarget, section: 'tax' })}>Tax Entries</SectionHeader>
                  <Stack spacing={2}>
                    {taxes.map((t, i) => (
                      <Paper key={i} variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <FormControl size="small" fullWidth sx={inputSx}>
                              <InputLabel>Type</InputLabel>
                              <Select value={t.tax_type} label="Type" onChange={(e) => updateTax(i, 'tax_type', e.target.value)}>
                                {TAX_TYPES.map((v) => <MenuItem key={v} value={v} sx={{ fontFamily: 'Heebo' }}>{v}</MenuItem>)}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              label="Rate (0–1)"
                              size="small"
                              type="number"
                              value={t.rate}
                              onChange={(e) => updateTax(i, 'rate', e.target.value)}
                              inputProps={{ step: '0.01', min: 0, max: 1 }}
                              error={!!errors[`tax_rate_${i}`]}
                              helperText={errors[`tax_rate_${i}`]}
                              fullWidth
                              sx={inputSx}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField label="Region" size="small" value={t.region} onChange={(e) => updateTax(i, 'region', e.target.value)} fullWidth sx={inputSx} />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField label="Zip Code" size="small" value={t.zip_code} onChange={(e) => updateTax(i, 'zip_code', e.target.value)} fullWidth sx={inputSx} />
                          </Grid>
                          <Grid item xs={4}>
                            <TextField label="Priority" size="small" type="number" value={t.priority} onChange={(e) => updateTax(i, 'priority', e.target.value)} inputProps={{ min: 0 }} fullWidth sx={inputSx} />
                          </Grid>
                          <Grid item xs={4}>
                            <FormControlLabel
                              control={<Checkbox checked={t.is_inclusive} onChange={(e) => updateTax(i, 'is_inclusive', e.target.checked)} sx={{ color: '#065F46', '&.Mui-checked': { color: '#065F46' } }} />}
                              label={<Typography sx={{ fontFamily: 'Heebo', fontSize: '13px' }}>Inclusive</Typography>}
                            />
                          </Grid>
                          <Grid item xs={4}>
                            <FormControlLabel
                              control={<Checkbox checked={t.is_active} onChange={(e) => updateTax(i, 'is_active', e.target.checked)} sx={{ color: '#065F46', '&.Mui-checked': { color: '#065F46' } }} />}
                              label={<Typography sx={{ fontFamily: 'Heebo', fontSize: '13px' }}>Active</Typography>}
                            />
                          </Grid>
                        </Grid>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                          <Button size="small" color="error" onClick={() => setTaxes((prev) => prev.filter((_, idx) => idx !== i))} sx={{ fontFamily: 'Heebo', textTransform: 'none' }}>
                            Remove
                          </Button>
                        </Box>
                      </Paper>
                    ))}
                    <Button
                      startIcon={<AddOutlinedIcon />}
                      onClick={() => setTaxes((prev) => [...prev, defaultTax()])}
                      sx={{ fontFamily: 'Heebo', textTransform: 'none', color: '#065F46', alignSelf: 'flex-start' }}
                    >
                      Add Tax
                    </Button>
                  </Stack>
                </Box>

                {/* Shipping Methods */}
                <Box>
                  <SectionHeader onInfo={(e) => setInfoPopover({ anchor: e.currentTarget, section: 'shipping' })}>Shipping Methods</SectionHeader>
                  <Stack spacing={2}>
                    {shippings.map((s, i) => (
                      <Paper key={i} variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <FormControl size="small" fullWidth sx={inputSx}>
                              <InputLabel>Method</InputLabel>
                              <Select value={s.method} label="Method" onChange={(e) => updateShipping(i, 'method', e.target.value)}>
                                {SHIP_METHODS.map((v) => <MenuItem key={v} value={v} sx={{ fontFamily: 'Heebo' }}>{formatMethod(v)}</MenuItem>)}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={6}>
                            <FormControlLabel
                              control={<Checkbox checked={s.is_active} onChange={(e) => updateShipping(i, 'is_active', e.target.checked)} sx={{ color: '#065F46', '&.Mui-checked': { color: '#065F46' } }} />}
                              label={<Typography sx={{ fontFamily: 'Heebo', fontSize: '13px' }}>Active</Typography>}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              label="Base Cost ($)"
                              size="small"
                              type="number"
                              value={s.base_cost}
                              onChange={(e) => updateShipping(i, 'base_cost', e.target.value)}
                              inputProps={{ step: '0.01', min: 0 }}
                              helperText="Display in $ (stored as cents)"
                              fullWidth
                              sx={inputSx}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              label="Free Above ($)"
                              size="small"
                              type="number"
                              value={s.free_above}
                              onChange={(e) => updateShipping(i, 'free_above', e.target.value)}
                              inputProps={{ step: '0.01', min: 0 }}
                              helperText="0 = never free"
                              fullWidth
                              sx={inputSx}
                            />
                          </Grid>
                        </Grid>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                          <Button size="small" color="error" onClick={() => setShippings((prev) => prev.filter((_, idx) => idx !== i))} sx={{ fontFamily: 'Heebo', textTransform: 'none' }}>
                            Remove
                          </Button>
                        </Box>
                      </Paper>
                    ))}
                    <Button
                      startIcon={<AddOutlinedIcon />}
                      onClick={() => setShippings((prev) => [...prev, defaultShipping()])}
                      sx={{ fontFamily: 'Heebo', textTransform: 'none', color: '#065F46', alignSelf: 'flex-start' }}
                    >
                      Add Shipping
                    </Button>
                  </Stack>
                </Box>

                {/* App Fee */}
                <Box>
                  <SectionHeader onInfo={(e) => setInfoPopover({ anchor: e.currentTarget, section: 'appfee' })}>App Fee</SectionHeader>
                  <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 1.5 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <TextField
                          label="Commission %"
                          size="small"
                          type="number"
                          value={appFee.percentage}
                          onChange={(e) => setAppFee((f) => ({ ...f, percentage: e.target.value }))}
                          inputProps={{ step: '0.01', min: 0, max: 100 }}
                          helperText="0–100"
                          error={!!errors.app_fee_pct}
                          fullWidth
                          sx={inputSx}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          label="Flat Fee ($)"
                          size="small"
                          type="number"
                          value={appFee.flat}
                          onChange={(e) => setAppFee((f) => ({ ...f, flat: e.target.value }))}
                          inputProps={{ step: '0.01', min: 0 }}
                          helperText="0 = no flat fee"
                          fullWidth
                          sx={inputSx}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <FormControl size="small" fullWidth sx={inputSx}>
                          <InputLabel>Apply To</InputLabel>
                          <Select value={appFee.apply_to} label="Apply To" onChange={(e) => setAppFee((f) => ({ ...f, apply_to: e.target.value }))}>
                            {APPLY_TO_OPTS.map((v) => <MenuItem key={v} value={v} sx={{ fontFamily: 'Heebo' }}>{formatMethod(v)}</MenuItem>)}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          label="Min Fee ($)"
                          size="small"
                          type="number"
                          value={appFee.min_fee}
                          onChange={(e) => setAppFee((f) => ({ ...f, min_fee: e.target.value }))}
                          inputProps={{ step: '0.01', min: 0 }}
                          error={!!errors.app_fee_min}
                          helperText={errors.app_fee_min}
                          fullWidth
                          sx={inputSx}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          label="Max Fee ($)"
                          size="small"
                          type="number"
                          value={appFee.max_fee}
                          onChange={(e) => setAppFee((f) => ({ ...f, max_fee: e.target.value }))}
                          inputProps={{ step: '0.01', min: 0 }}
                          helperText="0 = no cap"
                          fullWidth
                          sx={inputSx}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <FormControlLabel
                          control={<Checkbox checked={appFee.is_active} onChange={(e) => setAppFee((f) => ({ ...f, is_active: e.target.checked }))} sx={{ color: '#065F46', '&.Mui-checked': { color: '#065F46' } }} />}
                          label={<Typography sx={{ fontFamily: 'Heebo', fontSize: '13px' }}>Active</Typography>}
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                </Box>
              </Box>

              {/* Footer */}
              <Box sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                <Button onClick={onClose} sx={{ fontFamily: 'Heebo', textTransform: 'none', color: '#362B24' }}>
                  Cancel
                </Button>
                <Button
                  variant="outlined"
                  disabled={submitting}
                  onClick={() => handleSubmit(false)}
                  sx={{ fontFamily: 'Heebo', textTransform: 'none', borderRadius: '10px', color: '#065F46', borderColor: '#065F46', '&:hover': { borderColor: '#054d38', bgcolor: 'rgba(6,95,70,0.04)' } }}
                >
                  {submitting ? 'Creating…' : 'Create'}
                </Button>
                <Button
                  variant="contained"
                  disabled={submitting}
                  startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : null}
                  onClick={() => handleSubmit(true)}
                  sx={{ bgcolor: '#065F46', fontFamily: 'Heebo', textTransform: 'none', borderRadius: '10px', '&:hover': { bgcolor: '#054d38' } }}
                >
                  {submitting ? 'Creating…' : 'Create & Activate'}
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Drawer>
    </LocalizationProvider>

    <Popover
      open={Boolean(infoPopover.anchor)}
      anchorEl={infoPopover.anchor}
      onClose={() => setInfoPopover({ anchor: null, section: null })}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      PaperProps={{
        sx: { mt: 0.5, borderRadius: '14px', boxShadow: '0 8px 32px rgba(0,0,0,0.10)', border: '1px solid #f0f0f0', maxWidth: 440, p: 2.5 },
      }}
    >
      {infoPopover.section && (() => {
        const info = SECTION_INFO[infoPopover.section];
        return (
          <Box>
            <Typography sx={{ fontFamily: 'Heebo', fontSize: '12.5px', color: '#555', mb: 1.5, lineHeight: 1.55 }}>
              {info.intro}
            </Typography>
            <Box>
              {info.fields.map((f, i) => (
                <Box key={i} sx={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 1.5, py: 0.75, borderTop: '1px solid #f0f0f0' }}>
                  <Typography sx={{ fontFamily: 'Heebo', fontWeight: 600, fontSize: '12px', color: '#362B24' }}>{f.name}</Typography>
                  <Typography sx={{ fontFamily: 'Heebo', fontSize: '12px', color: '#555', lineHeight: 1.55 }}>{f.desc}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        );
      })()}
    </Popover>
    </ThemeProvider>
  );
}
